'use client';

import { useSession } from 'next-auth/react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { resolvePricingPlanSlug, type PlanSlug, type SelectablePlanSlug } from '@/app/pricing/pricing-plans';
import { PublicLink } from '@/components/PublicLink';
import { RecurringConsentBlock } from '@/components/billing/recurring-consent-block';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useBillingActions } from '@/hooks/use-billing-actions';
import type { IndexedPublicPlanPrices } from '@/lib/public-plan-prices-snapshot';
import { formatPublicPlanCadence, formatPublicPlanPrice } from '@/lib/public-plan-prices';
import type { SubscriptionInfo } from '@/types/billing';

type PlanAction =
  | { type: 'link'; label: string; href: string; disabled?: boolean }
  | {
      type: 'checkout';
      label: string;
      plan: SelectablePlanSlug;
      mode?: 'subscription' | 'setup';
      disabled?: boolean;
    };

type PricingPlansActionContextValue = {
  getPlanAction: (planSlug: PlanSlug) => PlanAction;
  handleCheckout: (planSlug: SelectablePlanSlug, mode?: 'subscription' | 'setup') => Promise<void>;
  pendingPlan: SelectablePlanSlug | null;
  subscriptionError: string | null;
};

const PricingPlansActionContext = createContext<PricingPlansActionContextValue | null>(null);

function usePricingPlansActionContext() {
  const context = useContext(PricingPlansActionContext);

  if (!context) {
    throw new Error('Pricing plan actions must be used inside PricingPlansActionProvider');
  }

  return context;
}

function resolveAnonymousAction(planSlug: PlanSlug): PlanAction {
  switch (planSlug) {
    case 'hobby':
      return { type: 'link', label: 'Начать бесплатно', href: '/signup' };
    case 'basic':
      return { type: 'link', label: 'Начать с Базового', href: '/signup?plan=basic' };
    case 'pro':
      return { type: 'link', label: 'Начать с Про', href: '/signup?plan=pro' };
    case 'custom':
      return { type: 'link', label: 'Связаться с продажами', href: '/contact' };
  }
}

export function PricingPlansActionProvider({
  children,
  publicPlanPrices,
}: {
  children: ReactNode;
  publicPlanPrices: IndexedPublicPlanPrices;
}) {
  const { data: session, status } = useSession();
  const isAuthenticated = status === 'authenticated';
  const user = session?.user ?? null;
  const { startCheckout } = useBillingActions();
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [pendingPlan, setPendingPlan] = useState<SelectablePlanSlug | null>(null);
  const [recurringConsentPlan, setRecurringConsentPlan] = useState<'basic' | 'pro' | null>(null);
  const [hasRecurringConsent, setHasRecurringConsent] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setSubscription(null);
      setSubscriptionError(null);
      return;
    }

    let cancelled = false;

    const loadSubscription = async () => {
      setSubscriptionError(null);

      try {
        const response = await fetch('/api/billing/subscription', { cache: 'no-store' });
        if (cancelled) {
          return;
        }

        if (!response.ok) {
          const message =
            response.status === 401
              ? 'Войдите снова, чтобы управлять оплатой.'
              : 'Сейчас не удалось загрузить ваш текущий тариф.';
          setSubscription(null);
          setSubscriptionError(message);
          return;
        }

        const payload = (await response.json()) as SubscriptionInfo | null;
        setSubscription(payload);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load subscription summary', error);
          setSubscription(null);
          setSubscriptionError('Сейчас не удается загрузить данные по оплате.');
        }
      }
    };

    loadSubscription();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const currentPlanSlug = useMemo<PlanSlug | undefined>(() => {
    return resolvePricingPlanSlug(subscription?.plan_slug) ?? resolvePricingPlanSlug(user?.plan);
  }, [subscription?.plan_slug, user?.plan]);

  const isPendingVerification = subscription?.status === 'pending_verification';
  const isAccountBlocked = subscription?.status === 'blocked_duplicate';

  const launchCheckout = useCallback(
    async (planSlug: SelectablePlanSlug, mode?: 'subscription' | 'setup', source?: 'pricing') => {
      setPendingPlan(planSlug);
      try {
        await startCheckout({
          plan: planSlug,
          mode,
          recurring_consent: source ? { accepted: true, source } : undefined,
        });
      } catch {
        setPendingPlan(null);
      }
    },
    [startCheckout],
  );

  const handleCheckout = useCallback(
    async (planSlug: SelectablePlanSlug, mode?: 'subscription' | 'setup') => {
      if (pendingPlan) {
        return;
      }

      const resolvedMode = mode ?? (planSlug === 'hobby' ? 'setup' : 'subscription');
      if (resolvedMode === 'subscription' && planSlug !== 'hobby') {
        setRecurringConsentPlan(planSlug);
        setHasRecurringConsent(false);
        return;
      }

      await launchCheckout(planSlug, resolvedMode);
    },
    [launchCheckout, pendingPlan],
  );

  const handleRecurringDialogChange = (open: boolean) => {
    if (pendingPlan) {
      return;
    }
    if (!open) {
      setRecurringConsentPlan(null);
      setHasRecurringConsent(false);
    }
  };

  const handleConfirmRecurringCheckout = async () => {
    if (!recurringConsentPlan || pendingPlan) {
      return;
    }

    await launchCheckout(recurringConsentPlan, 'subscription', 'pricing');
  };

  const recurringConsentDetails = recurringConsentPlan
    ? publicPlanPrices[recurringConsentPlan]
    : null;

  const contextValue = useMemo<PricingPlansActionContextValue>(() => {
    const getPlanAction = (planSlug: PlanSlug): PlanAction => {
      if (planSlug === 'custom') {
        return resolveAnonymousAction(planSlug);
      }

      if (!isAuthenticated) {
        return resolveAnonymousAction(planSlug);
      }

      if (planSlug === 'hobby') {
        if (currentPlanSlug === 'hobby' && isPendingVerification) {
          return { type: 'checkout', label: 'Подтвердить карту', plan: 'hobby', mode: 'setup' };
        }

        if (currentPlanSlug === 'hobby') {
          return { type: 'link', label: 'Текущий тариф', href: '#', disabled: true };
        }

        return {
          type: 'link',
          label: 'Понизить в разделе оплаты',
          href: '/billing',
          disabled: isAccountBlocked,
        };
      }

      if (isAccountBlocked) {
        return { type: 'link', label: 'Аккаунт заблокирован', href: '#', disabled: true };
      }

      if (currentPlanSlug === planSlug) {
        return { type: 'link', label: 'Текущий тариф', href: '#', disabled: true };
      }

      return {
        type: 'checkout',
        label: planSlug === 'basic' ? 'Перейти на Базовый' : 'Перейти на Про',
        plan: planSlug,
      };
    };

    return {
      getPlanAction,
      handleCheckout,
      pendingPlan,
      subscriptionError,
    };
  }, [
    currentPlanSlug,
    handleCheckout,
    isAccountBlocked,
    isAuthenticated,
    isPendingVerification,
    pendingPlan,
    subscriptionError,
  ]);

  return (
    <PricingPlansActionContext.Provider value={contextValue}>
      {children}
      <Dialog open={Boolean(recurringConsentPlan)} onOpenChange={handleRecurringDialogChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Подтвердите автосписания</DialogTitle>
            <DialogDescription>
              Перед запуском платного тарифа нужно отдельно подтвердить согласие на ежемесячные автоматические списания по условиям оферты.
            </DialogDescription>
          </DialogHeader>
          {recurringConsentDetails ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-[color:var(--site-border-soft)] p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[var(--site-ink-muted)]">
                      Выбранный тариф
                    </p>
                    <p className="text-sm font-semibold text-[var(--site-ink-strong)]">
                      {recurringConsentDetails.plan_name}
                    </p>
                  </div>
                  <p className="whitespace-nowrap text-sm font-semibold text-[var(--site-ink-strong)]">
                    {formatPublicPlanPrice(recurringConsentDetails)} / месяц
                  </p>
                </div>
              </div>

              <RecurringConsentBlock
                checked={hasRecurringConsent}
                onCheckedChange={setHasRecurringConsent}
                amountLabel={formatPublicPlanPrice(recurringConsentDetails)}
                frequencyLabel={formatPublicPlanCadence(recurringConsentDetails)}
                checkboxId="pricing-recurring-consent"
              />

              <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
                <Button
                  variant="outline"
                  onClick={() => handleRecurringDialogChange(false)}
                  disabled={pendingPlan !== null}
                >
                  Не сейчас
                </Button>
                <Button
                  onClick={handleConfirmRecurringCheckout}
                  disabled={!hasRecurringConsent || pendingPlan !== null}
                >
                  {pendingPlan && recurringConsentPlan === pendingPlan ? 'Переадресация...' : 'Перейти к оплате'}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </PricingPlansActionContext.Provider>
  );
}

export function PricingPlanActionButton({
  planSlug,
  highlighted = false,
}: {
  planSlug: PlanSlug;
  highlighted?: boolean;
}) {
  const { getPlanAction, handleCheckout, pendingPlan } = usePricingPlansActionContext();
  const action = getPlanAction(planSlug);
  const selectableSlug = planSlug === 'custom' ? null : (planSlug as SelectablePlanSlug);
  const isProcessing = selectableSlug ? pendingPlan === selectableSlug : false;
  const isOtherCheckoutLocked =
    action.type === 'checkout' &&
    selectableSlug !== null &&
    pendingPlan !== null &&
    pendingPlan !== selectableSlug;
  const buttonDisabled = Boolean(action.disabled) || isProcessing || isOtherCheckoutLocked;
  const buttonLabel = isProcessing ? 'Переадресация...' : action.label;
  const buttonClassName = highlighted
    ? 'h-11 w-full bg-[#1c2c5a] text-white shadow-md hover:bg-[#24376e]'
    : buttonDisabled
      ? 'h-11 w-full border-none bg-[color:rgba(23,38,59,0.12)] text-[var(--site-ink)] hover:bg-[color:rgba(23,38,59,0.14)]'
      : 'h-11 w-full border-[color:var(--site-border-soft)] bg-white text-[var(--site-ink)] hover:bg-[var(--site-surface-inset)]';
  const buttonVariant = highlighted ? 'default' : 'outline';

  if (action.type === 'checkout') {
    return (
      <Button
        className={buttonClassName}
        variant={buttonVariant}
        disabled={buttonDisabled}
        onClick={buttonDisabled ? undefined : () => handleCheckout(action.plan, action.mode)}
      >
        {buttonLabel}
      </Button>
    );
  }

  if (!buttonDisabled) {
    return (
      <PublicLink href={action.href}>
        <Button className={buttonClassName} variant={buttonVariant}>
          {buttonLabel}
        </Button>
      </PublicLink>
    );
  }

  return (
    <Button className={buttonClassName} variant={buttonVariant} disabled>
      {buttonLabel}
    </Button>
  );
}

export function PricingSubscriptionError() {
  const { subscriptionError } = usePricingPlansActionContext();

  if (!subscriptionError) {
    return null;
  }

  return <p className="text-center text-sm text-red-600">{subscriptionError}</p>;
}
